//
import {
  Alert,
  AlertTitle,
  Card,
  CardContent,
  Tooltip,
  Typography,
} from "@mui/material";
//
import { Button } from "@mui/material";
import { Form, Formik, FormikHelpers } from "formik";

import { Box, Stack } from "@mui/material";

import { Role } from "data/models";

import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import { UpgradeSubscriptionButton } from "components/action-buttons/action-buttons";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { PermissionsReasons } from "components/permissions/permission-messages";
import {
  PermissionsWrapper,
  PermissionTooltipButton,
} from "components/permissions/permissions";
import React, { useState } from "react";

interface UserCardProps {
  name: string;
  email: string;
  status: string;
  added: string;
}

interface RoleSelectorProps {
  memberRole: Role;
  onSubmit: {
    mutate: (
      variables: { role: string },
      options?: {
        onSuccess?: () => void;
        onError?: () => void;
      }
    ) => void;
  };
}

type RoleSelectorFormValues = {
  selectedRole: Role;
};

export const RoleSelector = ({ memberRole, onSubmit }: RoleSelectorProps) => {
  const [isEditing, setIsEditing] = useState(false);

  const roles = [
    {
      value: Role.VIEWER,
      label: "Viewer",
      description: "Read-only access.",
    },
    {
      value: Role.ANALYST,
      label: "Analyst",
      description: "Technical user with access to Datasets and Clip.",
    },
    {
      value: Role.DEVELOPER,
      label: "Developer",
      description:
        "Technical user with access to development-related features.",
    },
    {
      value: Role.BILLING,
      label: "Billing",
      description: "Access to billing and subscription management.",
    },
    {
      value: Role.ADMIN,
      label: "Admin",
      description: "Full control over the organisation.",
    },
    ...(memberRole === Role.OWNER
      ? [
          {
            value: Role.OWNER,
            label: "Owner",
            description: "Full control over the organisation.",
          },
        ]
      : []),
  ];

  const handleSubmit = (
    values: RoleSelectorFormValues,
    { setSubmitting }: FormikHelpers<RoleSelectorFormValues>
  ) => {
    onSubmit.mutate(
      { role: values.selectedRole },
      {
        onSuccess: () => {
          setSubmitting(false);
          setIsEditing(false);
        },
        onError: () => {
          setSubmitting(false);
        },
      }
    );
  };

  const handleCancel = (resetForm: () => void) => {
    resetForm();
    setIsEditing(false);
  };

  return (
    <Card style={{ width: "100%" }}>
      <Formik<RoleSelectorFormValues>
        enableReinitialize
        initialValues={{ selectedRole: memberRole }}
        onSubmit={handleSubmit}
      >
        {({
          values,
          setFieldValue,
          resetForm,
          isSubmitting,
          errors,
          touched,
        }) => (
          <Form>
            <CardContent>
              <FormControl
                component="fieldset"
                error={touched.selectedRole && !!errors.selectedRole}
              >
                <RadioGroup
                  value={values.selectedRole}
                  onChange={(event) => {
                    if (isEditing) {
                      setFieldValue("selectedRole", event.target.value);
                    }
                  }}
                >
                  {roles.map((role) => (
                    <Box key={role.value} sx={{ mb: 1 }}>
                      <FormControlLabel
                        value={role.value}
                        control={<Radio disabled={!isEditing} />}
                        label={
                          <Box>
                            <Typography variant="body1">
                              {role.label}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {role.description}
                            </Typography>
                          </Box>
                        }
                      />
                    </Box>
                  ))}
                </RadioGroup>
                <PermissionsWrapper
                  keys={[PermissionsKeys.rolesUpdate]}
                  messages={{
                    [PermissionsReasons.ROLE_DENY]: <></>,
                    [PermissionsReasons.UPGRADE_REQUIRED]: (
                      <>
                        <Alert
                          variant="outlined"
                          severity="info"
                          action={
                            <UpgradeSubscriptionButton
                              variant="text"
                              color="inherit"
                              style={{
                                justifyContent: "flex-end",
                                textAlign: "right",
                                width: 140,
                              }}
                              text="Review Plan"
                            />
                          }
                        >
                          <AlertTitle>
                            {"Organise Your Members with Roles"}
                          </AlertTitle>
                          Unlock powerful member management capabilities with
                          Roles, available exclusively on our Professional and
                          Enterprise plans.
                        </Alert>
                      </>
                    ),
                  }}
                >
                  <></>
                </PermissionsWrapper>
              </FormControl>
            </CardContent>

            <Stack
              direction="row"
              spacing={2}
              justifyContent="flex-end"
              sx={{ padding: "8px" }}
            >
              {isEditing ? (
                <>
                  <Button
                    size="small"
                    type="button"
                    variant="text"
                    onClick={() => handleCancel(resetForm)}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Saving..." : "Save"}
                  </Button>
                </>
              ) : (
                <Tooltip
                  title={
                    memberRole === Role.OWNER
                      ? "Transfer ownership to another member to change the owner of this organisation."
                      : ""
                  }
                >
                  <span>
                    <PermissionTooltipButton
                      permissionKeys={[PermissionsKeys.rolesUpdate]}
                      size="small"
                      variant="contained"
                      onClick={() => setIsEditing(true)}
                      isDisabled={memberRole === Role.OWNER}
                    >
                      Edit
                    </PermissionTooltipButton>
                  </span>
                </Tooltip>
              )}
            </Stack>
          </Form>
        )}
      </Formik>
    </Card>
  );
};

export const ResetTwoFactorCard = () => {
  return (
    <Card sx={{ width: "100%" }}>
      <CardContent>
        <Box sx={{ paddingLeft: "12px" }}>
          <Typography variant="body2" color="text.secondary">
            Reset two-factor authentication
          </Typography>

          <Typography variant="body1">
            Resetting two-factor authentication will remove all two-factor
            authentication methods for this member.
          </Typography>
        </Box>
      </CardContent>
      <Box sx={{ padding: "8px", textAlign: "right" }}>
        <Button variant="contained" onClick={() => {}} disabled={true}>
          RESET TWO-FACTOR AUTH
        </Button>
      </Box>
    </Card>
  );
};

export const UserCard: React.FC<UserCardProps> = ({
  name,
  email,
  status,
  added,
}) => {
  return (
    <Card>
      <CardContent>
        <Box sx={{ padding: "0px 12px 12px 12px" }}>
          <Typography variant="body2" color="text.secondary">
            Name
          </Typography>
          <Typography variant="h6">{name}</Typography>
        </Box>

        <Box sx={{ padding: "0px 12px 12px 12px" }}>
          <Typography variant="body2" color="text.secondary">
            Email
          </Typography>
          <Typography variant="body1">{email}</Typography>
        </Box>

        <Box sx={{ padding: "0px 12px 12px 12px" }}>
          <Typography variant="body2" color="text.secondary">
            Status
          </Typography>
          <Typography variant="body1">{status}</Typography>
        </Box>

        <Box sx={{ padding: "0px 12px 0px 12px" }}>
          <Typography variant="body2" color="text.secondary">
            Added
          </Typography>
          <Typography variant="body1">{added}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
};
